import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { latestDownloadsQueryOptions, type DownloadsMap } from '#api/downloads.queries.ts';
import { ValidationError } from '#api/errors.ts';
import { DownloadArrow } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { OSIcon } from '#components/atoms/cards/os-card/OSIcon.tsx';
import { Code } from '#components/atoms/code/Code.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { QRCode } from '#components/atoms/qr/QRCode.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import { RadioGroup } from '#components/patterns/forms/radio/index.ts';
import { useRadioGroup } from '#components/patterns/forms/radio/useRadioGroup.ts';
import { SelectGroup } from '#components/patterns/forms/select/select-group/SelectGroup.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { Select } from '#components/patterns/forms/select/select/Select.tsx';
import type { OSGroup } from '#dn-types/downloads.ts';
import { binaryInstallCommand } from '#screens/hosts/enroll/docs/binary-install.ts';
import { dockerComposeFile, dockerRunCommand } from '#screens/hosts/enroll/docs/container-install.ts';
import { mobileEnrollLink } from '#screens/hosts/enroll/docs/mobile-enroll-link.ts';
import {
  ARCHITECTURES_POPULAR,
  chainedPackageInstall,
  PACKAGE_OPTIONS,
  type PackageManager,
} from '#screens/hosts/enroll/docs/package-install.ts';
import { buildEnrollCommand } from '#screens/hosts/enroll/docs/token-instructions/build-enroll-command.ts';
import type { HostCreation } from './GetStarted.queries.ts';
import { Waiting } from './Waiting.tsx';
import styles from './InstallSection.module.css';

const OS_TABS = ['Linux', 'macOS', 'Windows', 'Mobile', 'Other'] as const satisfies readonly OSGroup[];

// Package installs bring their own service setup, so the chain is just repo setup → install → enroll.
const packageInstallCommand = (manager: PackageManager, code: string) => [
  ...chainedPackageInstall(manager),
  buildEnrollCommand('Linux', code, manager),
];

function installErrorMessage(error: Error | null): string {
  // ValidationError.message is a raw serialized dump; surface the reworded per-detail message
  // (e.g. host-limit/quota copy) instead. Everything else falls back to a friendly generic.
  const fallback = 'We were unable to set up this host. Please try again.';
  if (error instanceof ValidationError) {
    return error.errors[0]?.message ?? fallback;
  }
  return fallback;
}

/**
 * The "install + enroll the host" section. While the host + enrollment code are being created we
 * can't show a command yet; once it resolves the OS tabs render the real, per-host enrollment code.
 * A failure shows inline with a retry. Download links and commands come from the same downloads
 * query the enroll page uses, so the variant list can't drift from what we actually ship.
 */
export function InstallSection({
  creation,
  onRetry,
  os,
  onOSChange,
}: {
  creation: HostCreation;
  onRetry: () => void;
  /** Controlled by the flow: it snapshots each host's OS choice to tailor the done-step ping test. */
  os: OSGroup;
  onOSChange: (os: OSGroup) => void;
}) {
  const downloads = useQuery(latestDownloadsQueryOptions);
  // Per-instance so two copies of the flow in one document don't share a radio group or
  // cross-associate their labels through duplicate ids.
  const osGroupName = `install-os-${useId()}`;

  if (creation.isError) {
    return (
      <div className={styles.InstallSection_installBody}>
        <ErrorMessage>{installErrorMessage(creation.error)}</ErrorMessage>
        <Button variant="secondary" size="small" className="mt-8" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }

  if (!creation.data) {
    return <Waiting label="Setting up your host…" />;
  }

  const code = creation.data.enrollmentCode.code;

  return (
    <>
      <RadioGroup name={osGroupName} value={os} onChange={(e) => onOSChange(e.currentTarget.value as OSGroup)}>
        <div className={styles.InstallSection_osTabs}>
          {OS_TABS.map((option) => (
            <OSTab key={option} os={option} />
          ))}
        </div>
      </RadioGroup>
      <div className={styles.InstallSection_installBody}>
        {/* Data-first: a failed background refetch leaves the query in 'error' with cached data
            still present (the cache is shared with the enroll page) — renderable data wins. */}
        {downloads.data ?
          os === 'Linux' ?
            <LinuxInstall downloads={downloads.data} code={code} />
          : os === 'macOS' || os === 'Windows' ?
            <DesktopInstall os={os} downloads={downloads.data} code={code} />
          : os === 'Mobile' ?
            <MobileInstall downloads={downloads.data} code={code} />
          : <OtherInstall downloads={downloads.data} code={code} />
        : downloads.isError ?
          <>
            <ErrorMessage>We were unable to load the download options. Please try again.</ErrorMessage>
            <Button
              variant="secondary"
              size="small"
              className="mt-8"
              disabled={downloads.isRefetching}
              onClick={() => downloads.refetch()}
            >
              Try again
            </Button>
          </>
        : <Waiting label="Loading download options…" />}
      </div>
    </>
  );
}

// One OS switcher pill. Follows the RadioCard pattern (visually-hidden native radio + styled
// label) so the group carries real radio semantics — announced mutual exclusivity, arrow-key
// navigation — like the enroll page's ArchitecturePicker, with the pill styling unchanged.
function OSTab({ os }: { os: OSGroup }) {
  const { name, selectedValue, onChange } = useRadioGroup();
  const selected = selectedValue === os;
  const id = `${name}-${os}`;

  return (
    <>
      <input
        className={`visually-hidden ${styles.InstallSection_osTabRadio}`}
        id={id}
        name={name}
        type="radio"
        value={os}
        checked={selected}
        onChange={onChange}
      />
      <label
        htmlFor={id}
        className={
          selected ?
            `${styles.InstallSection_osTab} ${styles.InstallSection_osTab___selected}`
          : styles.InstallSection_osTab
        }
      >
        <OSIcon os={os} />
        {os}
      </label>
    </>
  );
}

// One numbered step (circle + optional connecting line + titled body) in the guided installs.
function NumStep({
  n,
  title,
  hasLine,
  children,
}: {
  n: number;
  title: string;
  hasLine?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li className={styles.InstallSection_numStep}>
      <div className={styles.InstallSection_numStepMarker}>
        <span className={styles.InstallSection_numStepCircle}>{n}</span>
        {hasLine ?
          <span className={styles.InstallSection_numStepLine} />
        : null}
      </div>
      <div className={styles.InstallSection_numStepBody}>
        <div className={styles.InstallSection_numStepTitle}>{title}</div>
        {children}
      </div>
    </li>
  );
}

// Linux: pick a package repository (preferred) or a binary build, then run the enroll command —
// a guided two-step layout.
function LinuxInstall({ downloads, code }: { downloads: DownloadsMap; code: string }) {
  // Only arches the API serves a build for can produce a runnable command.
  const options = downloads.Linux.filter((a) => a.download !== undefined);
  const popular = options.filter((a) => ARCHITECTURES_POPULAR.includes(a.key));
  const rest = options.filter((a) => !ARCHITECTURES_POPULAR.includes(a.key));
  const [choice, setChoice] = useState<string>('apt');
  const packageManager = PACKAGE_OPTIONS.find((p) => p.key === choice)?.key;
  const selected = options.find((a) => a.key === choice);
  const handleChoice = (value: string) => {
    if (value) {
      setChoice(value);
    }
  };

  return (
    <ol className={styles.InstallSection_numSteps}>
      <NumStep n={1} title="Select install method" hasLine={true}>
        <Select
          label="Install method"
          visuallyHideLabel={true}
          defaultValue="apt"
          onChange={handleChoice}
          renderValue={(value: string) =>
            PACKAGE_OPTIONS.find((p) => p.key === value)?.label ??
            options.find((a) => a.key === value)?.label ??
            'Please select…'
          }
        >
          <SelectGroup label="Package manager" hasBottomBorder={true}>
            {PACKAGE_OPTIONS.map((p) => (
              <SelectItem key={p.key} value={p.key}>
                {p.label}
              </SelectItem>
            ))}
          </SelectGroup>
          <SelectGroup label="Popular binaries" hasBottomBorder={true}>
            {popular.map((a) => (
              <SelectItem key={a.key} value={a.key}>
                {a.label}
              </SelectItem>
            ))}
          </SelectGroup>
          {rest.map((a) => (
            <SelectItem key={a.key} value={a.key}>
              {a.label}
            </SelectItem>
          ))}
        </Select>
      </NumStep>
      <NumStep n={2} title="Run the enroll command">
        {packageManager ?
          <Snippet>{packageInstallCommand(packageManager, code)}</Snippet>
        : selected?.download ?
          <Snippet>{binaryInstallCommand('Linux', selected.download, code)}</Snippet>
        : <p className="p-md color-lowlight mb-0">Select an install method to see the enroll command.</p>}
      </NumStep>
    </ol>
  );
}

// macOS & Windows: download the desktop app, then enroll with a code — a guided two-step layout.
function DesktopInstall({ os, downloads, code }: { os: 'macOS' | 'Windows'; downloads: DownloadsMap; code: string }) {
  const macDesktop = downloads.macOS.find((a) => a.key === 'macos-universal-desktop');
  const windowsDesktops = downloads.Windows.filter(
    (a) => a.key === 'windows-amd64-desktop' || a.key === 'windows-arm64-desktop'
  );

  return (
    <ol className={styles.InstallSection_numSteps}>
      <NumStep n={1} title="Download the app" hasLine={true}>
        {os === 'macOS' ?
          macDesktop?.url ?
            <>
              <LinkButton variant="secondary" size="xsmall" href={macDesktop.url} className="flex gap-8">
                <DownloadArrow width={16} height={16} aria-hidden={true} /> Download DNClient Desktop
              </LinkButton>
              <div className={styles.InstallSection_downloadMeta}>Universal</div>
            </>
          : null
        : <div className="flex gap-8">
            {windowsDesktops.map((a) =>
              a.url ?
                <LinkButton key={a.key} variant="secondary" size="xsmall" href={a.url} className="flex gap-8">
                  <DownloadArrow width={16} height={16} aria-hidden={true} /> Download for{' '}
                  {a.label.replace(' Desktop', '')}
                </LinkButton>
              : null
            )}
          </div>
        }
      </NumStep>
      <NumStep n={2} title="Open it and choose Enroll device">
        <div className={styles.InstallSection_enrollCode}>
          <p className="p-md color-lowlight mb-0">Then paste this single-use enrollment code (expires in 24 hours):</p>
          <Snippet className="m-0">{code}</Snippet>
        </div>
      </NumStep>
    </ol>
  );
}

// Mobile: get the app from a store, then scan or open the link to enroll — a guided two-step layout.
function MobileInstall({ downloads, code }: { downloads: DownloadsMap; code: string }) {
  const ios = downloads.Mobile.find((a) => a.key === 'ios');
  const android = downloads.Mobile.find((a) => a.key === 'android');
  const enrollLink = mobileEnrollLink(code);

  return (
    <ol className={styles.InstallSection_numSteps}>
      <NumStep n={1} title="Download the app" hasLine={true}>
        <div className="flex gap-8">
          {ios?.url ?
            <LinkButton variant="secondary" size="xsmall" href={ios.url}>
              App Store
            </LinkButton>
          : null}
          {android?.url ?
            <LinkButton variant="secondary" size="xsmall" href={android.url}>
              Google Play
            </LinkButton>
          : null}
        </div>
      </NumStep>
      {/* A QR code cannot be scanned by the screen showing it, so the same link is also a button. */}
      <NumStep n={2} title="Scan or click the link to enroll">
        <p className="p-md color-lowlight mb-0">
          Scan the QR code with the device you’re enrolling, or open the link to enroll the device you’re on. Codes
          expire in 24 hours.
        </p>
        <QRCode
          className="width-fit-content"
          value={enrollLink}
          title="QR code containing the enrollment link"
          small={true}
        />
        <div className="flex gap-8">
          {/* New tab on purpose: this is a cross-origin navigation, and the wizard's progress lives in component state. */}
          <LinkButton variant="secondary" size="xsmall" href={enrollLink} target="_blank" rel="noreferrer">
            Enroll this device
          </LinkButton>
        </div>
      </NumStep>
    </ol>
  );
}

// Other: containers and BSD — pick a platform, then run the command.
function OtherInstall({ downloads, code }: { downloads: DownloadsMap; code: string }) {
  // Like Linux: only platforms the API actually serves a build for are selectable.
  const options = downloads.Other.filter((a) => a.type !== 'download' || a.download !== undefined);
  const [platform, setPlatform] = useState('docker');
  const selected = options.find((a) => a.key === platform);
  const handlePlatform = (value: string) => {
    if (value) {
      setPlatform(value);
    }
  };

  return (
    <ol className={styles.InstallSection_numSteps}>
      <NumStep n={1} title="Select platform" hasLine={true}>
        <Select
          label="Platform"
          visuallyHideLabel={true}
          defaultValue="docker"
          onChange={handlePlatform}
          renderValue={(value) => options.find((a) => a.key === value)?.label ?? 'Please select…'}
        >
          {options.map((a) => (
            <SelectItem key={a.key} value={a.key}>
              {a.label}
            </SelectItem>
          ))}
        </Select>
      </NumStep>
      <NumStep n={2} title="Run the enroll command">
        {selected?.key === 'docker' ?
          <Snippet>{dockerRunCommand(code)}</Snippet>
        : selected?.key === 'docker-compose' ?
          <>
            <Snippet>{dockerComposeFile(code)}</Snippet>
            <p className="p-md color-lowlight mb-0">
              Save this as <Code>docker-compose.yaml</Code>, then run <Code>docker compose up -d</Code>.
            </p>
          </>
        : selected?.type === 'download' && selected.download ?
          <Snippet>{binaryInstallCommand('FreeBSD', selected.download, code)}</Snippet>
        : <p className="p-md color-lowlight mb-0">Select a platform to see the enroll command.</p>}
      </NumStep>
    </ol>
  );
}
